/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { Row, Col, CardBody } from 'reactstrap';
import { Button, Field, CardTitle, useFeatures } from '@cordata/buffet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LocationForm from '~/components/Location/MultiForm';
import { FieldArray, useFormikContext } from 'formik';
import { authorizedPyInstance as axiosPy, referenceServices } from '~/utils';

const PatientsAddFields = () => {
  const [reasons, setReasons] = useState([]);
  const [genders, setGenders] = useState([]);
  const { hasAppCommunity } = useFeatures();
  const [isBirthDateRequired, setIsBirthDateRequired] = useState(!hasAppCommunity);
  const [genderIdentities, setGenderIdentities] = useState([]);
  const { setFieldValue, values } = useFormikContext();
  const [referralSource, setReferralSource] = useState([]);

  const PRIMARY_PHONES = [
    { label: 'Mobile', value: 'mobile' },
    { label: 'Home', value: 'home' },
    { label: 'Work', value: 'work' },
  ];

  useEffect(() => {
    axiosPy
      .get('api/data/reference/reference-reason?ref_data_type=inbound referral')
      .then(r => r.data.data)
      .then(r => setReasons(r));

    referenceServices.genderFetch().then(r => setGenders(r));

    referenceServices.genderIdentityFetch().then(r => setGenderIdentities(r));

    referenceServices.referralSourceFetch().then(r => setReferralSource(r));
  }, []);

  useEffect(() => {
    if (!values.gender) {
      return;
    }

    const newGi = genderIdentities.find(gi => gi.genderIdentityName === values.gender.genderName);
    if (newGi) {
      setFieldValue('genderIdentity', newGi);
    }
  }, [values.gender]);

  useEffect(() => {
    // because useFeatures doesn't return instantly
    setIsBirthDateRequired(!hasAppCommunity);
  }, [hasAppCommunity]);

  return (
    <React.Fragment>
      <CardBody>
        <CardTitle>Basic Information</CardTitle>

        <Row form>
          <Col md>
            <Field name="firstName" required />
          </Col>

          <Col md>
            <Field name="middleName" />
          </Col>

          <Col md>
            <Field name="lastName" required />
          </Col>
        </Row>

        <Row form>
          <Col>
            <Field
              name="birthDate"
              type="date"
              required={isBirthDateRequired}
            />
          </Col>

          <Col>
            <Field
              name="gender"
              type="select"
              options={genders}
              getOptionValue={o => o.genderUid}
              getOptionLabel={o => o.genderName}
              required
            />
          </Col>
          <Col>
            <Field
              name="genderIdentity"
              type="select"
              options={genderIdentities}
              getOptionValue={o => o.genderIdentityUid}
              getOptionLabel={o => o.genderIdentityName}
            />
          </Col>
        </Row>
      </CardBody>

      <hr />

      <CardBody>
        <CardTitle>Contact Information</CardTitle>

        <Row form>
          <Col>
            <FieldArray
              name="emails"
              render={({ form: { values: v } }) =>
                v.emails.map(() => (
                  <Field
                    name={`emails.${0}.emailAddress`}
                    label="Email"
                    placeholder="Enter Email"
                    type="email"
                  />
                ))
              }
            />
          </Col>
        </Row>

        <Row form>
          <Col xl>
            <Field
              name="primaryPhone"
              type="radio-group"
              options={PRIMARY_PHONES}
              required
              inline
            />
          </Col>

          <Col xl>
            <Field
              name="mobilePhone"
              type="tel"
              required={values.primaryPhone === 'mobile'}
            />
          </Col>

          <Col xl>
            <Field
              name="homePhone"
              type="tel"
              required={values.primaryPhone === 'home'}
            />
          </Col>

          <Col xl>
            <Field
              name="workPhone"
              type="tel"
              required={values.primaryPhone === 'work'}
            />
          </Col>
        </Row>
      </CardBody>

      <hr />

      <CardBody>
        <CardTitle>Location Information</CardTitle>

        <FieldArray
          name="addresses"
          render={({
 push, remove, form, form: { values: v },
}) => (
  <>
    {v.addresses &&
                v.addresses.map((a, i) => (
                  <React.Fragment key={i}>
                    <Button
                      size="sm"
                      outline
                      color="danger"
                      spear
                      type="button"
                      onClick={() => remove(i)}
                    >
                      <FontAwesomeIcon icon="minus" />
                      <span className="d-none d-md-inline ml-2">
                        Remove Address
                      </span>
                    </Button>

                    <LocationForm {...form} index={i} />

                    {i !== v.addresses.length - 1 && <hr />}
                  </React.Fragment>
                ))}

    <Button
      size="sm"
      outline
      color="tertiary"
      spear
      type="button"
      onClick={() => push({ postCode: '' })}
    >
      <FontAwesomeIcon icon="plus" />
      <span className="d-none d-md-inline ml-2">Add Address</span>
    </Button>
  </>
          )}
        />
      </CardBody>

      <hr />

      <CardBody>
        <CardTitle>Referral Details</CardTitle>

        <Row form>
          <Col>
            <Field name="referralDate" type="date" required />
          </Col>

          <Col>
            <Field
              name="referralReason"
              type="select"
              options={reasons}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.reasonLabel}
              required
            />
          </Col>
        </Row>

        <Row form>
          <Col>
            {referralSource.length > 5 ? (
              <Field
                name="referralSource"
                type="select"
                options={referralSource}
                getOptionValue={o => o.referralSourceUid}
                getOptionLabel={o => o.name}
                required
              />
            ) : (
              <Field
                name="referralSource"
                type="radio-group"
                options={referralSource}
                required
                inline
              />
            )}
          </Col>

          <Col>
            <Field name="referredTo" type="hidden" required />
          </Col>
        </Row>
      </CardBody>
    </React.Fragment>
  );
};

export default PatientsAddFields;
